import { RoomPhase } from "../../../api/types";
import type { GameMode, RoomInfo } from "../../../api/types";

/** "All" entry of the mode and map filters. */
export const ALL = "all";
export type ModeFilter = GameMode | typeof ALL;

export interface RoomFilters {
  mode: ModeFilter;
  /** A map id or `ALL`. */
  map: string;
  hideFull: boolean;
  hideEmpty: boolean;
  /** Matched case-insensitively against the room name and the host name. */
  query: string;
}

export const DEFAULT_ROOM_FILTERS: RoomFilters = {
  mode: ALL,
  map: ALL,
  hideFull: false,
  hideEmpty: false,
  query: "",
};

export function filtersActive(f: RoomFilters): boolean {
  return f.mode !== ALL || f.map !== ALL || f.hideFull || f.hideEmpty || f.query.trim() !== "";
}

export function isRoomFull(room: RoomInfo): boolean {
  return room.players >= room.maxPlayers;
}

/** Why a room cannot be joined right now, or null when it can. */
export function joinBlockReason(room: RoomInfo): string | null {
  if (room.phase === RoomPhase.ENDED) return "This match has ended";
  if (isRoomFull(room)) return "This room is full";
  return null;
}

export function filterRooms(rooms: readonly RoomInfo[], f: RoomFilters): RoomInfo[] {
  const query = f.query.trim().toLowerCase();
  return rooms.filter((r) => {
    if (f.mode !== ALL && r.mode !== f.mode) return false;
    if (f.map !== ALL && r.map !== f.map) return false;
    if (f.hideFull && isRoomFull(r)) return false;
    if (f.hideEmpty && r.players <= 0) return false;
    if (query === "") return true;
    return r.name.toLowerCase().includes(query) || (r.host ?? "").toLowerCase().includes(query);
  });
}

export type RoomSortKey = "name" | "mode" | "map" | "players" | "phase" | "time" | "lock" | "region";
export type SortDir = "asc" | "desc";

export interface RoomSort {
  key: RoomSortKey;
  dir: SortDir;
}

export const DEFAULT_ROOM_SORT: RoomSort = { key: "players", dir: "desc" };

/** Direction applied when a column is selected for the first time. */
const FIRST_DIR: Record<RoomSortKey, SortDir> = {
  name: "asc",
  mode: "asc",
  map: "asc",
  players: "desc",
  phase: "asc",
  time: "asc",
  lock: "asc",
  region: "asc",
};

/** Header click: the active column flips direction, another column starts at its first direction. */
export function nextSort(current: RoomSort, key: RoomSortKey): RoomSort {
  if (current.key === key) return { key, dir: current.dir === "asc" ? "desc" : "asc" };
  return { key, dir: FIRST_DIR[key] };
}

/** Display names of ids that the catalog resolves (mode and map columns sort by what the user reads). */
export interface RoomLabels {
  modeName: (mode: GameMode) => string;
  mapName: (id: string) => string;
}

const PHASE_ORDER: Record<RoomPhase, number> = {
  [RoomPhase.WARMUP]: 0,
  [RoomPhase.LIVE]: 1,
  [RoomPhase.ENDED]: 2,
};

const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

/** Ascending comparison on the sort column alone. */
function comparePrimary(a: RoomInfo, b: RoomInfo, key: RoomSortKey, labels: RoomLabels): number {
  switch (key) {
    case "name":
      return collator.compare(a.name, b.name);
    case "mode":
      return collator.compare(labels.modeName(a.mode), labels.modeName(b.mode));
    case "map":
      return collator.compare(labels.mapName(a.map), labels.mapName(b.map));
    case "players":
      return a.players - b.players;
    case "phase":
      return PHASE_ORDER[a.phase] - PHASE_ORDER[b.phase];
    case "time":
      return (a.timeLeftSec ?? 0) - (b.timeLeftSec ?? 0);
    case "lock":
      return Number(a.hasPassword) - Number(b.hasPassword);
    case "region":
      return collator.compare(a.region, b.region);
  }
}

/**
 * Sorted copy. Ties fall back to name then id (always ascending), so rows keep their place across
 * refreshes whatever order the server returns them in. Rooms without a clock sort last in both
 * directions of the time column.
 */
export function sortRooms(rooms: readonly RoomInfo[], sort: RoomSort, labels: RoomLabels): RoomInfo[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rooms].sort((a, b) => {
    if (sort.key === "time") {
      if (a.timeLeftSec === undefined && b.timeLeftSec !== undefined) return 1;
      if (a.timeLeftSec !== undefined && b.timeLeftSec === undefined) return -1;
    }
    return (
      comparePrimary(a, b, sort.key, labels) * sign || collator.compare(a.name, b.name) || collator.compare(a.id, b.id)
    );
  });
}
